import { EmployeeAvatar } from '@newfront-insurance/core-ui';
import { X } from 'lucide-react';
import type { ClipboardEvent } from 'react';
import { useEffect, useId, useMemo, useState } from 'react';

import {
  getEmailAddresses,
  getEmployeeDisplayName,
  getEmployeeInitials,
  type SettingsEmployee,
} from './employee-utils';
import { PEOPLE_RESULT_LIMIT, useSettingsEmployeeSearch } from './use-employee-search';

export interface SettingsPeopleEligibility {
  allowedUserUuids?: ReadonlySet<string>;
  loading: boolean;
  error?: string;
  retry: () => void;
}

export function SettingsPeoplePicker({
  excludedUserUuids,
  eligibility,
  inputId,
  selectedEmployees,
  onAddEmployee,
  onRemoveEmployee,
  disabled = false,
}: {
  excludedUserUuids: Set<string>;
  eligibility?: SettingsPeopleEligibility;
  inputId?: string;
  selectedEmployees: SettingsEmployee[];
  onAddEmployee: (employee: SettingsEmployee) => void;
  onRemoveEmployee: (userUuid: string) => void;
  disabled?: boolean;
}) {
  const resultsId = useId();
  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [activeIndex, setActiveIndex] = useState(0);
  const [pendingPastedEmails, setPendingPastedEmails] = useState<string[]>([]);
  const { employees, loading: isEmployeeSearchLoading, error, retry } = useSettingsEmployeeSearch(query, !disabled);
  const isLoading = isEmployeeSearchLoading || !!eligibility?.loading;
  const searchError = eligibility?.error || (error ? "Couldn't search people." : undefined);
  const retrySearch = eligibility?.error ? eligibility.retry : retry;
  const allowedUserUuids = eligibility?.allowedUserUuids;
  // Filter the bounded search response. A more specific query may find eligible people beyond this batch.
  const matchingEmployees = useMemo(
    () =>
      isLoading || searchError
        ? []
        : employees.filter((employee) => !allowedUserUuids || allowedUserUuids.has(employee.userUuid)),
    [allowedUserUuids, employees, isLoading, searchError],
  );
  const selectedUserUuids = useMemo(
    () => new Set(selectedEmployees.map((employee) => employee.userUuid)),
    [selectedEmployees],
  );
  const visibleEmployees = useMemo(() => {
    return matchingEmployees
      .filter((employee) => !excludedUserUuids.has(employee.userUuid) && !selectedUserUuids.has(employee.userUuid))
      .slice(0, PEOPLE_RESULT_LIMIT);
  }, [excludedUserUuids, matchingEmployees, selectedUserUuids]);
  const activeEmployee = isLoading || searchError ? undefined : (visibleEmployees[activeIndex] ?? visibleEmployees[0]);

  useEffect(() => {
    if (disabled || pendingPastedEmails.length === 0 || isLoading || searchError) {
      return;
    }

    const pendingEmailSet = new Set(pendingPastedEmails);
    const resolvedEmployees = matchingEmployees.filter(
      (employee) => employee.email && pendingEmailSet.has(employee.email.toLowerCase()),
    );

    if (resolvedEmployees.length === 0) {
      return;
    }

    const resolvedEmailSet = new Set<string>();
    for (const employee of resolvedEmployees) {
      const normalizedEmail = employee.email.toLowerCase();
      resolvedEmailSet.add(normalizedEmail);

      if (!excludedUserUuids.has(employee.userUuid) && !selectedUserUuids.has(employee.userUuid)) {
        onAddEmployee(employee);
      }
    }

    const remainingEmails = pendingPastedEmails.filter((email) => !resolvedEmailSet.has(email));
    setPendingPastedEmails(remainingEmails);
    setQuery(remainingEmails.join(', '));
    setIsOpen(remainingEmails.length > 0);
  }, [
    disabled,
    excludedUserUuids,
    isLoading,
    searchError,
    matchingEmployees,
    onAddEmployee,
    pendingPastedEmails,
    selectedUserUuids,
  ]);

  const handleSearchChange = (nextQuery: string) => {
    setQuery(nextQuery);
    setActiveIndex(0);
    setPendingPastedEmails([]);
    setIsOpen(true);
  };

  const handlePaste = (event: ClipboardEvent<HTMLInputElement>) => {
    const pastedEmails = getEmailAddresses(event.clipboardData.getData('text'));

    if (pastedEmails.length === 0) {
      return;
    }

    event.preventDefault();
    setPendingPastedEmails(pastedEmails);
    setQuery(pastedEmails.join(', '));
    setIsOpen(true);
  };

  const handleSelectEmployee = (employee: SettingsEmployee) => {
    onAddEmployee(employee);
    setQuery('');
    setPendingPastedEmails([]);
    setIsOpen(false);
  };

  return (
    <div className="relative">
      <div
        className={[
          'flex min-h-10 w-full flex-wrap items-center gap-1.5 rounded-[4px] border border-[#BEC7D0] bg-white',
          'px-2 py-1.5 focus-within:border-[#0957C3]',
        ].join(' ')}
      >
        {selectedEmployees.map((employee) => {
          const employeeName = getEmployeeDisplayName(employee);

          return (
            <span
              key={employee.userUuid}
              className="inline-flex h-7 max-w-full items-center gap-1 rounded-[4px] bg-[#E2EBF1] px-2 font-['Open_Sans',sans-serif] text-[13px] leading-5 font-semibold text-[#262D46]"
            >
              <span className="max-w-[132px] truncate">{employeeName}</span>
              <button
                type="button"
                disabled={disabled}
                aria-label={`Remove ${employeeName}`}
                className="inline-flex h-4 w-4 items-center justify-center rounded-full text-[#546A83] hover:bg-[#D5E0EA] hover:text-[#262D46]"
                onClick={() => onRemoveEmployee(employee.userUuid)}
              >
                <X aria-hidden="true" className="h-3 w-3" />
              </button>
            </span>
          );
        })}
        <input
          id={inputId}
          role="combobox"
          aria-label="Search people by name or email"
          aria-expanded={isOpen && !disabled}
          aria-controls={isOpen && !disabled ? resultsId : undefined}
          aria-autocomplete="list"
          aria-activedescendant={
            isOpen && !disabled && activeEmployee ? `${resultsId}-${activeEmployee.userUuid}` : undefined
          }
          disabled={disabled}
          value={query}
          onBlur={() => window.setTimeout(() => setIsOpen(false), 150)}
          onChange={(event) => handleSearchChange(event.target.value)}
          onFocus={() => setIsOpen(true)}
          onPaste={handlePaste}
          onKeyDown={(event) => {
            if (event.key === 'Enter') {
              // Selecting a teammate must not submit the containing team form.
              event.preventDefault();
              if (isOpen && activeEmployee) handleSelectEmployee(activeEmployee);
            } else if (event.key === 'Escape') {
              event.preventDefault();
              setIsOpen(false);
            } else if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
              event.preventDefault();
              setIsOpen(true);
              setActiveIndex((index) =>
                Math.max(0, Math.min(visibleEmployees.length - 1, index + (event.key === 'ArrowDown' ? 1 : -1))),
              );
            }
          }}
          placeholder={selectedEmployees.length > 0 ? '' : 'Search by name or email'}
          className="h-7 min-w-[140px] flex-1 border-0 bg-transparent px-1 py-0 font-['Open_Sans',sans-serif] text-[14px] leading-5 font-normal text-[#262D46] outline-none placeholder:text-[#98A6B5]"
        />
      </div>

      {isOpen && !disabled ? (
        <div
          id={resultsId}
          role="listbox"
          className="absolute top-[calc(100%+4px)] right-0 left-0 z-10 max-h-64 overflow-auto rounded-[4px] border border-[#DEE2E7] bg-white py-1 shadow-lg"
        >
          {isLoading ? (
            <p className="px-3 py-3 text-sm text-[#546A83]">Searching people...</p>
          ) : searchError ? (
            <p role="alert" className="px-3 py-3 text-sm text-[#546A83]">
              {searchError}{' '}
              <button type="button" onClick={retrySearch} className="font-semibold underline">
                Retry
              </button>
            </p>
          ) : !query.trim() ? (
            <p className="px-3 py-3 text-sm text-[#546A83]">Start typing a name or email.</p>
          ) : visibleEmployees.length > 0 ? (
            visibleEmployees.map((employee) => {
              const employeeName = getEmployeeDisplayName(employee);

              return (
                <button
                  key={employee.userUuid}
                  id={`${resultsId}-${employee.userUuid}`}
                  type="button"
                  role="option"
                  aria-selected={false}
                  aria-label={`Select ${employeeName}${employee.email ? ` ${employee.email}` : ''}`}
                  className={`flex w-full items-center gap-3 px-3 py-2 text-left hover:bg-[#F4F8FE] ${employee === activeEmployee ? 'bg-[#F4F8FE]' : ''}`}
                  onMouseDown={(event) => event.preventDefault()}
                  onClick={() => handleSelectEmployee(employee)}
                >
                  <EmployeeAvatar
                    sizeInPixels={32}
                    imageUrl={employee.imageUrl}
                    initials={getEmployeeInitials(employee, employee.userUuid)}
                  />
                  <span className="min-w-0">
                    <span className="block truncate text-sm font-semibold text-[#262D46]">{employeeName}</span>
                    {employee.email ? (
                      <span className="block truncate text-xs text-[#546A83]">{employee.email}</span>
                    ) : null}
                  </span>
                </button>
              );
            })
          ) : (
            <p className="px-3 py-3 text-sm text-[#546A83]">
              {allowedUserUuids
                ? 'No matching organization members found. Only members of this organization can be added.'
                : 'No people found.'}
            </p>
          )}
        </div>
      ) : null}
    </div>
  );
}
